import * as _ from "lodash";
import * as turf from "@turf/turf";
import {Feature} from "@turf/turf";
import * as mapboxgl from "mapbox-gl";
import {DISTANCE_LABEL_SOURCE_TYPE} from "@core/consts/map.constants";
import {State} from "../../map-config.reducer";
import {DistanceUnits} from "@core/model/user-map";

export const showLineDrawings = (state: State, action): State => {
  const drawFeaturesCollection: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: []
  };
  drawFeaturesCollection.features = _.cloneDeep(state.drawingFeatures.features);

  for (const mapLine of state.mapLines) {
    const line = turf.lineString([mapLine.pointA, mapLine.pointB]);
    const distanceKm = turf.length(line, {units: 'kilometers'});
    const latencyMs = 2 * (1 + (distanceKm / 200));
    const formattedLatency = latencyMs.toFixed(1);

    const distancelabel = `${numberWithCommas(turf.length(line, {units: state.units}).toFixed(0))} ` +
      `${state.units == DistanceUnits.MILES ? 'miles' : 'km'}\n` +
      `Latency (RTT) ≈ ${formattedLatency} ms`;

    const lineFeature: Feature = {
      type: "Feature",
      id: mapLine.auxId,
      geometry: {
        type: "LineString",
        coordinates: [mapLine.pointA, mapLine.pointB]
      },
      properties: {
        label: distancelabel
      }
    };
    drawFeaturesCollection.features.push(lineFeature as any);
  }

  return {
    ...state,
    displayLines: true,
    mapLines: [],
    drawingFeatures: drawFeaturesCollection,
  };
}

export const updateDistanceUnits = (state: State, action): State => {
  const drawFeaturesCollection: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: []
  };

  // Safe access to mapSource.data with fallback
  const mapSourceData = state.mapSource.data ? _.cloneDeep(state.mapSource.data) : { type: "FeatureCollection" as const, features: [] };
  const mapSource: mapboxgl.GeoJSONSourceRaw = {
    type: 'geojson',
    data: mapSourceData
  };

  // Process drawing features (safe access)
  if (state.drawingFeatures && state.drawingFeatures.features) {
    for (let feature of state.drawingFeatures.features) {
      if (feature.geometry.type == "LineString") {
        let line = turf.lineString((feature.geometry as any).coordinates);
        const distanceKm = turf.length(line, {units: 'kilometers'});
        const latencyMs = 2 * (1 + (distanceKm / 200));
        const formattedLatency = latencyMs.toFixed(1);

        const distancelabel = `${numberWithCommas(turf.length(line, {units: action.units}).toFixed(0))} ` +
          `${action.units == DistanceUnits.MILES ? 'miles' : 'km'}\n` +
          `Latency (RTT) ≈ ${formattedLatency} ms`;

        const newLine = _.cloneDeep(feature);
        newLine.properties['label'] = distancelabel;
        drawFeaturesCollection.features.push(newLine);
      } else {
        drawFeaturesCollection.features.push(feature);
      }
    }
  }

  // Process map source features (safe access)
  if (mapSourceData && (mapSourceData as any).features) {
    for (let feature of (mapSourceData as any).features) {
      if (feature.geometry && feature.geometry.type == "LineString") {
        let line = turf.lineString((feature.geometry as any).coordinates);
        const distanceKm = turf.length(line, {units: 'kilometers'});
        const latencyMs = 2 * (1 + (distanceKm / 200));
        const formattedLatency = latencyMs.toFixed(1);

        const distancelabel = `${numberWithCommas(turf.length(line, {units: action.units}).toFixed(0))} ` +
          `${action.units == DistanceUnits.MILES ? 'miles' : 'km'}\n` +
          `Latency (RTT) ≈ ${formattedLatency} ms`;
        feature.properties['label'] = distancelabel;
      }
    }
  }

  return {
    ...state,
    units: action.units
  };
}

export const updateDistanceLabel = (state: State, action): State => {
  var line = turf.lineString((action.feature.geometry as any).coordinates);
  const distanceKm = turf.length(line, {units: 'kilometers'});
  const latencyMs = 2 * (1 + (distanceKm / 200));
  const formattedLatency = latencyMs.toFixed(1);

  const distancelabel = `${numberWithCommas(turf.length(line, {units: state.units}).toFixed(0))} ` +
    `${state.units == DistanceUnits.MILES ? 'miles' : 'km'}\n` +
    `Latency (RTT) ≈ ${formattedLatency} ms`;

  const exFeature = action.drawControlFeatureCollection.features.filter(f => f.id == action.feature.id)[0];

  const lineFeature: Feature = {
    id: exFeature.id,
    type: "Feature",
    geometry: {
      type: "LineString",
      coordinates: (exFeature.geometry as any).coordinates
    },
    properties: {
      label: distancelabel
    }
  };

  const drawFeaturesCollection: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: []
  };
  drawFeaturesCollection.features = _.cloneDeep(action.drawControlFeatureCollection.features.filter(f => f.id != action.feature.id));
  drawFeaturesCollection.features.push(lineFeature as any);

  const mapSource: mapboxgl.GeoJSONSourceRaw = {
    type: 'geojson',
    data: _.cloneDeep(state.mapSource.data)
  };

  const existingFeature = (mapSource.data as any).features.filter(
    f => f.properties["id"] && action.feature.id == f.properties["id"]);
  if (existingFeature.length) {
    existingFeature[0].properties['label'] = distancelabel;
    existingFeature[0].geometry = action.feature.geometry;
  } else {
    const pointFeature: Feature = {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: (action.feature.geometry as any).coordinates
      },
      properties: {
        label: distancelabel,
        id: action.feature.id,
        type: DISTANCE_LABEL_SOURCE_TYPE
      }
    };
    (mapSource.data as any).features.push(pointFeature);
  }

  return {
    ...state,
    drawingFeatures: drawFeaturesCollection,
    mapSource: mapSource
  };
}

const numberWithCommas = (x) => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
